import React from "react";
import { SyntheticEvent, useCallback, useEffect, useRef } from "react";
import { areShallowEqualFull } from "../core/areEqual";
import { PackEditorCallbacks } from "../core/EditorTypes";
import { Concept, Pack, PackAttachment, RepresentationMap } from "../Types";
import RepView from "./RepView";

function test() {
  // do something!~
}

type ConceptViewProps = {
  index: number;
  reps: RepresentationMap;
  selected: boolean;
  selection: [number, number, number] | undefined; // don't send if unchanged
  selectedCols: string[];
  isSuggested: boolean;


  packEditorCallbacks: PackEditorCallbacks;
  packAttachments: PackAttachment[];
  
  // addRepCb: (c: Concept, value: string) => void;
  // removeRepCb: (c: Concept, value: string) => void;
  // removeConceptCb: (c: Concept) => void;
  // updateRepField: (
  //   concept: Concept,
  //   repKey: string,
  //   fieldKey: string,
  //   value: any,
  // ) => void;
  // updateConceptId: (concept: Concept, newId: string) => void;
  concept: Concept;
  setSelection: (repIndex: number, repKeyIndex: number) => void;
  getSelectedElement: (repIndex: number, repKeyIndex: number) => void;
  style: React.CSSProperties;

};

const ConceptViewUnmemoized = (props: ConceptViewProps) => {
  // keyboard shortcuts
  const {
    index,
    reps,
    concept,
    selected,
    selectedCols,
    selection,
    style,
    setSelection,
    getSelectedElement,
    packAttachments
  } = props;

  const { packEditorCallbacks } = props;

  const { 
    removeConceptCb,
    addRepCb,
    removeRepCb,
    updateRepField,
    updateConceptId,
   } = packEditorCallbacks;

  // console.log("selection", selection);
  const repKeys = Object.keys(concept.reps);

  const missingReps = Object.keys(reps).filter(
    (x) => !repKeys.includes(x),
  );

  const missingRepInputs = missingReps.map(
    (x) => <option key={x} value={x}>{x}</option>,
  );

  const idInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!selected || (selection != null && selection[1] !== 0)) {
      return;
    }
    idInputRef?.current?.click();
    idInputRef?.current?.focus();
    console.log('selecting id')
  }, [selected, selection]);

  const addConceptRepCb = useCallback(() => {
    addRepCb(concept, dropdownRef.current!.value);
  }, [concept.reps]);

  const removeConceptRepCb = useCallback(
    (rep) => {
      removeRepCb(concept, rep);
    },
    [concept.reps],
  );

  const removeCb = useCallback(() => {
    removeConceptCb(concept);
  }, [concept]);

  const updateRepFieldChild = useCallback(
    (repKey: string, fieldKey: string, value: any) => {
      updateRepField(concept, repKey, fieldKey, value);
    },
    [concept, updateRepField],
  );

  const onChangeConceptId = useCallback(
    (event: SyntheticEvent) => {
      updateConceptId(concept, (event.target as HTMLInputElement).value);
    },
    [concept, updateConceptId],
  );

  const fields = selectedCols;

  const repViews = repKeys.map(
    (repKey, repIndex) => {
      // TODO HOOKIFY
      const setSelectionFromRep = (repKeyIndex: number) => {
        setSelection(repIndex + 1, repKeyIndex);
      };
      return (
        <RepView
          isAiSuggestion={(concept.tags || []).includes(`#aigen-rep-${repKey}`)}
          fields={fields}
          key={repKey}
          selected={selected && (selection![1] === (repIndex + 1))}
          selection={selection}
          setSelection={setSelectionFromRep}
          fullName={reps[repKey].promptText || repKey}
          name={repKey}
          rep={concept.reps[repKey]}
          removeRepCb={removeConceptRepCb}
          updateRepField={updateRepFieldChild}
          attachments={packAttachments}
        />
      );
    },
  );

  const selectId = useCallback(() => {
    // if (!selected) {
    //   return;
    // }
    // setSelection(0, selection![2]);
    setSelection(0, 0);
  }, [selected, selection, concept]);

  const dropdown = (
    <>
      <select ref={dropdownRef}>
        {missingRepInputs}
      </select>
      <div className="add-btn padleft" onClick={addConceptRepCb}>+</div>
    </>
  );

  const classes = `concept ${selected ? "selected" : ""}`;

  return (
    <div className={classes} style={style}>
      <button className="close-btn" onClick={removeCb}>❌</button>
      {concept?.tags?.includes("#aigen")
        ? (
          <div
            className=""
            style={{
              marginRight: "8px",
              padding: "8px",
              borderRadius: "8px",
              backgroundColor: concept?.tags.includes("#aigen-pending")
                ? "orange"
                : "green",
              opacity: 0.5,
              color: "white",
              float: "right",
            }}
          >
            🤖 AI SUGGESTION
          </div>
        )
        : <span></span>}
      <div className="">
        <span className="subtle">({index})</span>
        <input
          ref={idInputRef}
          className="subtle"
          type="text"
          value={concept.id}
          onFocus={selectId}
          onInput={onChangeConceptId}
        />
        <span className="subtle fade">{" "}(unique ID)</span>
      </div>
      <table>
        <tr>
          <th>rep</th>
          {fields.map((field) => <th key={field}>{field}</th>)}
        </tr>
        {repViews}
      </table>
      {missingReps.length > 0 ? dropdown : null}
    </div>
  );
};

export const ConceptView = React.memo(
  ConceptViewUnmemoized,
  areShallowEqualFull("concept", ["setSelection", "getSelectedElement"], (
    prevProps,
    nextProps,
  ) => {
    // check reps
    const prevReps = prevProps.reps;
    const nextReps = nextProps.reps;
    if (Object.keys(prevReps).length !== Object.keys(nextReps).length) {
      return false;
    }
    for (const repKey of Object.keys(prevReps)) {
      if (!nextReps[repKey]) {
        return false;
      }
      if (
        Object.keys(prevReps[repKey]).length !==
          Object.keys(nextReps[repKey]).length
      ) {
        return false;
      }
    }
    return true;
  }, false),
);
